import { useState } from 'preact/hooks';
import { activeDiagnostics, streamInspectorOpen } from '../state/diagnostics';
import { Icon } from './Icon';

export function StreamInspectorModal() {
  const [copied, setCopied] = useState(false);
  const diag = activeDiagnostics.value;

  if (!streamInspectorOpen.value || !diag) return null;

  const close = () => {
    streamInspectorOpen.value = false;
  };

  const handleCopy = async () => {
    const report = {
      timestamp: new Date().toISOString(),
      codec: diag.codec,
      bitrateKbps: diag.bitrateKbps,
      mimeType: diag.detectedMimeType,
      activeUrl: diag.activeSourceUrl,
      sourceHost: diag.sourceOrigin,
      bufferSeconds: diag.bufferSeconds,
      playbackState: diag.playbackState,
      isLiveStream: diag.isLiveStream,
      enclosureType: diag.enclosureType,
      enclosureLengthBytes: diag.enclosureLength,
      durationSeconds: diag.durationSec,
      alternateEnclosuresCount: diag.alternateEnclosures.length,
      alternateEnclosures: diag.alternateEnclosures,
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(report, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard write failed
    }
  };

  return (
    <div
      class="np"
      role="dialog"
      aria-modal="true"
      aria-label="Stream and Audio Diagnostics"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div class="np__sheet stream-inspector">
        <div class="stream-inspector__header">
          <div class="stream-inspector__title-row">
            <Icon name="info" size={20} />
            <h2 class="stream-inspector__title">Stream & Audio Diagnostics</h2>
          </div>
          <button
            type="button"
            class="icon-button stream-inspector__close"
            onClick={close}
            aria-label="Close diagnostics"
          >
            <Icon name="x" size={20} />
          </button>
        </div>

        <div class="stream-inspector__content">
          {/* Quick Metrics Cards */}
          <div class="stream-inspector__cards">
            <div class="stream-inspector__card">
              <span class="stream-inspector__card-label">Codec / Format</span>
              <span class="stream-inspector__card-val">{diag.codec}</span>
            </div>
            <div class="stream-inspector__card">
              <span class="stream-inspector__card-label">Bitrate</span>
              <span class="stream-inspector__card-val">
                {diag.bitrateKbps ? `${diag.bitrateKbps} kbps` : 'Unknown'}
              </span>
            </div>
            <div class="stream-inspector__card">
              <span class="stream-inspector__card-label">Buffer Window</span>
              <span class="stream-inspector__card-val">
                {diag.isLiveStream ? 'Live Edge' : `${diag.bufferSeconds}s`}
              </span>
            </div>
          </div>

          {/* Technical Runtime Table */}
          <section class="stream-inspector__section">
            <h3 class="stream-inspector__section-heading">Runtime Stream Data</h3>
            <div class="stream-inspector__table">
              <div class="stream-inspector__row">
                <span class="stream-inspector__key">MIME Content-Type:</span>
                <span class="stream-inspector__val stream-inspector__mono">{diag.detectedMimeType}</span>
              </div>
              <div class="stream-inspector__row">
                <span class="stream-inspector__key">Active Source Host:</span>
                <span class="stream-inspector__val">{diag.sourceOrigin || 'Local / Origin'}</span>
              </div>
              <div class="stream-inspector__row">
                <span class="stream-inspector__key">Playback Mode:</span>
                <span class="stream-inspector__val">
                  {diag.isLiveStream ? '🔴 Live Broadcast' : 'On-Demand Episode'}
                </span>
              </div>
              <div class="stream-inspector__row">
                <span class="stream-inspector__key">Stream URL:</span>
                <span class="stream-inspector__val stream-inspector__mono stream-inspector__break">
                  {diag.activeSourceUrl}
                </span>
              </div>
            </div>
          </section>

          {/* Declared Alternate Enclosures */}
          {diag.alternateEnclosures.length > 0 && (
            <section class="stream-inspector__section">
              <h3 class="stream-inspector__section-heading">
                Declared Alternate Enclosures ({diag.alternateEnclosures.length})
              </h3>
              <div class="stream-inspector__alt-list">
                {diag.alternateEnclosures.map((alt, idx) => (
                  <div key={idx} class="stream-inspector__alt-card">
                    <div class="stream-inspector__alt-header">
                      <span class="stream-inspector__badge">
                        {alt.codecs || alt.type || 'Alternate Source'}
                      </span>
                      {alt.bitrate && (
                        <span class="stream-inspector__bitrate">
                          {Math.round(alt.bitrate / 1000)} kbps
                        </span>
                      )}
                    </div>
                    {alt.sources && alt.sources.length > 0 && (
                      <div class="stream-inspector__alt-sources">
                        {alt.sources.map((s, sIdx) => (
                          <span key={sIdx} class="stream-inspector__alt-uri stream-inspector__mono">
                            {s.uri}
                          </span>
                        ))}
                      </div>
                    )}
                    {alt.integrity && (
                      <div class="stream-inspector__alt-integrity stream-inspector__mono">
                        SRI: {alt.integrity.type}-{alt.integrity.value?.slice(0, 16)}…
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Action Bar */}
          <div class="stream-inspector__actions">
            <button
              type="button"
              class="button button--outline stream-inspector__copy-btn"
              onClick={handleCopy}
            >
              {copied ? '✓ Copied to Clipboard' : '📋 Copy Stream Diagnostics'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
